import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { JobTargetCreationMethod } from '../constants-types/Enums';
import { BehavioralFactorRanges } from '../core-data-model/BehavioralFactorRanges.model';
import { CognitiveTargetQuestion } from '../core-data-model/CognitiveTargetQuestion.model';
import {
    Job,
    defaultBehavioralRangesSet,
    defaultCogScore,
    JobUpdate,
    JobWithEverything
} from '../core-data-model/Job.model';
import { JobAssessment } from '../core-data-model/JobAssessment.model';
import { JobChecklistQuestion } from '../core-data-model/JobChecklistQuestion.model';
import {
    SetJobTargetFlowType,
    SetJobTargetStep
} from '../core-data-model/SetJobTarget.interface';
import { JobApi } from '../core-services/JobApi/JobApi.connect';
import {
    notificationOnError,
    notification
} from '../core-services/Notifications.service';
import { SetJobTargetSaveService } from '../core-services/SetJobTargetSave.service';
import {
    StoreDataService,
    JobHydration
} from '../core-services/Store.dataservice';
import { WaitingIndicatorDecorator } from '../core-services/WaitingIndicator.decorator';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';

@Injectable({
    providedIn: 'root'
})
export class SetJobTargetFlowDataService {
    // This value is difficult to reason about. When you are not on a specific
    // set of pages this is a Job. When you go to a specific set of pages the
    // route resolver changes the type. Unraveling this is difficult so I am not
    // touching it much.
    jobId: string;
    isSubmittingJobTarget = false;
    useBenchmarkProfileAsTarget = false;
    public factorRanges: BehavioralFactorRanges;
    public cogScore: number;
    public cogWeight = 0.5;
    public cogScaledScore: number;
    public type: SetJobTargetFlowType;

    private behavioralQuestionsPageSize = 30;
    private cognitiveQuestionsPageSize = 4;

    behavioralPageCount = 3;

    // Default to manual - aka dragging ranges
    public jobTargetCreationMethod: JobTargetCreationMethod =
        JobTargetCreationMethod.Manual;

    private _checklistQuestions: JobChecklistQuestion[];
    private _cognitiveQuestions: CognitiveTargetQuestion[];

    senderTakesJobAssessment = false;
    inlineMultiJAResults: {
        behavioral: BehavioralFactorRanges;
        cognitive: number;
    };
    isCreatedFromJobCreateFlow = false;

    constructor(
        private jobApi: JobApi,
        private userProfile: UserProfileSettingsService,
        private saveService: SetJobTargetSaveService,
        private initData: InitDataService,
        private store: StoreDataService
    ) {}

    get job(): Job | JobWithEverything {
        return this.store.jobs.get(this.jobId);
    }

    get skipCognitive(): boolean {
        // TODO: remove when PI-4373 goes out
        if (this.senderTakesJobAssessment) {
            return true;
        }
        return !this.userProfile.showCognitive;
    }

    resolve(route: ActivatedRouteSnapshot): Promise<Job> {
        return new Promise((resolve, reject) => {
            if (!this.job && !route.params.id) {
                return reject();
            }
            if (this.job) {
                return resolve(this.job);
            }
            const jobId: string = route.params.id;
            return this.selectJobToTarget(jobId).then(() => resolve(this.job));
        });
    }

    saveProgress(step: SetJobTargetStep, page: number) {
        this.saveService.saveProgress(
            this.job.id,
            this._checklistQuestions,
            this._cognitiveQuestions,
            step,
            page,
            this.senderTakesJobAssessment
        );
    }

    startFlow() {
        this.clearFlow();
    }

    setType(type: SetJobTargetFlowType): void {
        this.type = type;
    }

    private clearMultiJAState(): void {
        this.senderTakesJobAssessment = false;
        this.inlineMultiJAResults = null;
    }

    clearQuestions() {
        this._checklistQuestions = null;
        this._cognitiveQuestions = null;
    }

    setJob(job: Job, isCreatedFromJobCreateFlow = false): void {
        this.jobId = job.id;
        this.useBenchmarkProfileAsTarget = false;
        this.isCreatedFromJobCreateFlow = isCreatedFromJobCreateFlow;
        // Handle null values
        const { behavioralRanges, cogScore } = Job.normalizeNullJobTarget(
            this.job
        );
        this.factorRanges = behavioralRanges;
        this.cogScore = cogScore;
    }

    clearFlow() {
        this.jobId = null;
        this.useBenchmarkProfileAsTarget = false;
        this.clearQuestions();
        this.clearMultiJAState();

        this.factorRanges = defaultBehavioralRangesSet;
        this.cogScore = defaultCogScore;
    }

    applySavedState() {
        const savedState = this.saveService.getSenderTakesJobAssessment(
            this.job.id
        );
        this.senderTakesJobAssessment =
            savedState !== null ? savedState : false;
    }

    getSetJobTargetRoute(route: string): string {
        return `/setJobTarget/${this.job.id}/` + route;
    }

    getSetJobTargetStartingRoute(job: Job): string {
        return this.getSetJobTargetRoute(
            job.hasBenchmark() ? 'startBenchmarkProfile' : 'start'
        );
    }

    private async selectJobToTargetFromJob(
        job: Job,
        routeToIfTargetSet: string
    ): Promise<string[]> {
        this.setJob(job);
        const hydratedJob = await this.store.jobs.hydrate(
            this.job,
            JobHydration.AddAssessmentsAndRoleModels
        );

        return this.getDestinationFromJob(
            hydratedJob,
            !!hydratedJob.assessments.length,
            routeToIfTargetSet
        );
    }

    /**
     * Refetch the job for the given id, set the flow, and return the proper
     * path to route to to set the job target.
     *
     * @param jobId
     * @param routeToIfTargetSet The path returned to route to if the job target is already set
     */
    async selectJobToTarget(
        jobId: string,
        routeToIfTargetSet = `/job/${jobId}`
    ): Promise<string[]> {
        // Note that I am refetching the job even if the job exists based on the
        // dropdown. this is to ensure that the data source for the page remains
        // consistent. This incurs a small latency cost, but is a good tradeoff
        // until we have a more consistent data layer
        const job = await this.getJobFromIdAndUpdateFlow(jobId);
        return this.selectJobToTargetFromJob(job, routeToIfTargetSet);
    }

    getGuidedPathFromJob(job: Job): string {
        const currentPageData = this.saveService.getLastPageForJob(job.id);
        if (!currentPageData) {
            return this.getSetJobTargetRoute('behavioral/1');
        }
        const stepString =
            currentPageData.step === SetJobTargetStep.Behavioral
                ? 'behavioral'
                : 'cognitive';
        return this.getSetJobTargetRoute(
            `${stepString}/${currentPageData.page}`
        );
    }

    private getDestinationFromJob(
        job: Job,
        hasJobAssessments: boolean,
        routeToIfTargetSet: string
    ): string[] {
        // If there's a target send em to the route passed in
        if (job.hasBehavioralJobTarget) {
            return [routeToIfTargetSet];
            // If JAs exist then send them to the multi ja review page
        } else if (hasJobAssessments) {
            return [this.getSetJobTargetRoute('multipleContributor/review')];
        } else if (this.saveService.inProgress(job.id)) {
            this.setType(SetJobTargetFlowType.Guided);
            return [this.getGuidedPathFromJob(this.job)];
        } else {
            return [this.getSetJobTargetRoute('start')];
        }
    }

    @WaitingIndicatorDecorator.blockScreenOnWait()
    async getJobFromIdAndUpdateFlow(jobId: string): Promise<Job> {
        const job = await this.store.jobs.fetch(jobId);
        this.store.jobs.hydrate(job, JobHydration.AddAssessments);
        this.setJob(job);
        return this.job;
    }

    // Handle fetching and storing the questions the first time
    private get checklistQuestions(): Promise<JobChecklistQuestion[]> {
        if (!this._checklistQuestions) {
            return this.jobApi
                .getChecklistQuestions()
                .then(
                    (checklistQuestions) =>
                        (this._checklistQuestions =
                            this.saveService.applySavedChangesToBehavioral(
                                this.job.id,
                                checklistQuestions
                            ))
                );
        }
        return Promise.resolve(this._checklistQuestions);
    }
    getBehavioralQuestions(page: number): Promise<JobChecklistQuestion[]> {
        return this.checklistQuestions.then((questions) =>
            questions.slice(
                (page - 1) * this.behavioralQuestionsPageSize,
                page * this.behavioralQuestionsPageSize
            )
        );
    }

    // Handle fetching and storing the questions the first time
    private get cognitiveQuestions(): Promise<CognitiveTargetQuestion[]> {
        if (!this._cognitiveQuestions) {
            return this.jobApi
                .getCognitiveQuestions()
                .then(
                    (questions) =>
                        (this._cognitiveQuestions =
                            this.saveService.applySavedChangesToCognitive(
                                this.job.id,
                                questions
                            ))
                );
        }
        return Promise.resolve(this._cognitiveQuestions);
    }
    getCognitiveQuestions(page: number): Promise<CognitiveTargetQuestion[]> {
        return this.cognitiveQuestions.then((questions) =>
            questions.slice(
                (page - 1) * this.cognitiveQuestionsPageSize,
                page * this.cognitiveQuestionsPageSize
            )
        );
    }

    /**
     * Returns the correct route to go to after hitting continue on the Behavioral
     * or Cognitive part of the Guided Set Job Target flow.
     */
    getContinueRoute(step: SetJobTargetStep): string {
        const isDone =
            step === SetJobTargetStep.Cognitive || this.skipCognitive;

        if (isDone) {
            // If the sender of a multi ja is doing the inline assessment, send them
            // to the review page when they're done
            return this.senderTakesJobAssessment
                ? this.getSetJobTargetRoute('multipleContributor/review')
                : // Otherwise send them to the job page
                  this.job.getJobPageUrl();
        }
        return '../../cognitive/1';
    }

    @notificationOnError()
    submitJobTargetQuestions(): Promise<void> {
        this.isSubmittingJobTarget = true;
        return Promise.all([
            this._checklistQuestions
                ? this.jobApi.getBehavioralJobScore(this._checklistQuestions)
                : null,
            this.userProfile.showCognitive
                ? this.jobApi.getCognitiveJobScore(
                      this._cognitiveQuestions,
                      this.job.onetSocCode
                  )
                : null
        ])
            .then(([behavioral, cognitive]) => {
                this.factorRanges = behavioral;
                this.cogScore = cognitive;
                // if cogWeight is 0 need to set it up
                this.cogWeight = 0.5;

                // Store the results if they're doing the inline multi JA
                if (this.senderTakesJobAssessment) {
                    this.inlineMultiJAResults = {
                        behavioral,
                        cognitive
                    };
                    return this.createAndScore(this._checklistQuestions).then(
                        () =>
                            this.selectJobToTarget(this.job.id).then(
                                () => undefined // Done for typing purposes
                            )
                    );
                    // Otherwise patch the job with the results
                    // This code path represents setting the job completely by the inline and
                    // not using the multi-JA
                } else {
                    // Set it again to be sure!
                    this.jobTargetCreationMethod =
                        JobTargetCreationMethod.SingleAssessment;
                    return this.updateJob();
                }
            })
            .finally(() => (this.isSubmittingJobTarget = false));
    }

    private createAndScore(checklist: JobChecklistQuestion[]): Promise<void> {
        const myself = this.initData.user;
        return this.jobApi.createAndScoreJobAssessments(
            this.job,
            myself,
            myself,
            checklist
        );
    }

    getNormalizedBaselineCogTarget(rawScore: number): Promise<number> {
        return this.jobApi
            .getCognitiveBaselineNormalizedJobScore(
                rawScore,
                this.job.onetSocCode
            )
            .then((jobCogScore) => {
                this.cogScaledScore = jobCogScore;
                return this.cogScaledScore;
            });
    }

    async updateJob(): Promise<void> {
        const patchJob = new JobUpdate({
            originalObject: this.job,
            jobTargetCreationMethod: this.jobTargetCreationMethod,
            behavioralRanges: this.factorRanges,
            cognitiveTarget: this.cogScore,
            cognitiveWeight: this.cogWeight
        });
        const newJob = await this.store.jobs.update(patchJob);
        this.setJob(newJob);
        this.saveService.clearSavedResponses(newJob.id);
    }

    @notification(
        'notification.title-success',
        'sendAssessment.saveSuccess-message'
    )
    sendReminder(jobAssessment: JobAssessment) {
        const hackedPerson = {
            email: jobAssessment.recipientEmail,
            fullName: () => jobAssessment.recipientName
        };
        return this.jobApi.createJobAssessments(this.job, this.initData.user, [
            hackedPerson
        ]);
    }

    // Making JobService a param so that this service which is called all over
    // the place doesn't rope in JobService as a dependency for *those* callers
    setJobRangesFromTopBenchmark(factorRanges: BehavioralFactorRanges) {
        if (factorRanges !== null) {
            this.factorRanges = factorRanges;

            this.useBenchmarkProfileAsTarget = true;

            this.jobTargetCreationMethod =
                JobTargetCreationMethod.BenchmarkProfile;
            // set to be BenchmarkProfile as the JobTargetType
            this.setType(SetJobTargetFlowType.BenchmarkProfile);
            // set the cognitive baseline score
            this.cogScore = this.job.cognitiveBaselineScaledScore;
        }
    }

    setBenchmarkProfileAsTarget(useBenchmarkProfileAsTarget: boolean) {
        this.useBenchmarkProfileAsTarget = useBenchmarkProfileAsTarget;
    }
}
